/**
 * The router.
 *
 * Built from the module registry, so the route table is a consequence of which modules
 * are registered rather than a second list that must be kept in step with the first.
 *
 * Every module route is wrapped once, here, in the suspense boundary its lazy chunk needs.
 * Doing it centrally means a module author cannot forget it, and a forgotten boundary is
 * a blank screen rather than a visible error.
 */

import { createBrowserRouter, type RouteObject } from 'react-router'

import { AppShell } from '../shell/AppShell'
import type { ModuleRegistry } from '../modules/registry'
import type { RuntimeConfig } from '../config/runtime'
import {
  LandingRedirect,
  NotFoundState,
  RequireAuth,
  RouteErrorBoundary,
  ScreenSuspense,
} from './guards'
import { PATHS } from './paths'
import { NoAccessState } from '../ui/states'

/**
 * Converts a module's declared `Component` into a suspense-wrapped `element`.
 *
 * Written as an explicit construction rather than a spread of the incoming route, because
 * a module route declares a path and a component and nothing else. Spreading an arbitrary
 * route object would carry loaders, actions, and error boundaries a module has no business
 * setting from here, and would let one module's mistake reshape the router.
 */
function wrap(route: RouteObject): RouteObject {
  if (route.path === undefined) {
    // The registry contract is that every module route declares a path. A pathless route
    // would mount as a layout route matching everything beneath its parent, silently
    // shadowing its siblings -- a failure that is far cheaper to find here than in a
    // browser.
    throw new Error('A module route was registered without a path.')
  }

  const children = route.children?.map(wrap)
  const Component = route.Component

  const element =
    Component === undefined || Component === null ? undefined : (
      <ScreenSuspense>
        <Component />
      </ScreenSuspense>
    )

  return {
    path: route.path,
    ...(element === undefined ? {} : { element }),
    ...(children === undefined ? {} : { children }),
  }
}

export function buildRoutes(
  registry: ModuleRegistry,
  config: RuntimeConfig,
): RouteObject[] {
  return [
    {
      path: PATHS.root,
      element: (
        <RequireAuth>
          <AppShell registry={registry} />
        </RequireAuth>
      ),
      // Attached at the shell level so a failing screen renders inside the shell, leaving
      // the navigation usable.
      ErrorBoundary: RouteErrorBoundary,
      children: [
        { index: true, element: <LandingRedirect registry={registry} /> },
        ...registry.routes(config).map(wrap),
        { path: PATHS.accessDenied, element: <NoAccessState /> },
        // A catch-all inside the shell rather than a bare page, so an out-of-date link
        // leaves the user somewhere they can navigate from.
        { path: '*', element: <NotFoundState /> },
      ],
    },
    // Sibling route objects, not children of the `RequireAuth`-wrapped tree above: a
    // module opts a route out of the authenticated shell by declaring it in its own
    // `publicRoutes` (see `platform/modules/registry.ts`), for a surface -- such as the
    // signed, single-use link an `approval_requested` email carries -- that a
    // signed-out visitor must be able to reach. The platform layer knows nothing about
    // which module that is; `RequireAuth` itself is unmodified, and every other route
    // still goes through it exactly as before.
    ...registry.publicRoutes(config).map((route) => ({
      ...wrap(route),
      ErrorBoundary: RouteErrorBoundary,
    })),
  ]
}

export function createRouter(
  registry: ModuleRegistry,
  config: RuntimeConfig,
): ReturnType<typeof createBrowserRouter> {
  return createBrowserRouter(buildRoutes(registry, config))
}
